// Incident management utilities
// CHANGE: updateIncidentStatus no longer sends resolved_at/resolved_by — a DB trigger sets them from auth.uid() and now()
// Handles incident reporting system for all authenticated users
// All roles can create incidents (operator, manager, director, admin)
// Visibility: operators see own incidents, managers see own + assigned sites, directors/admins see all
// Status modification: admins and managers can update status, directors and operators cannot
// UPDATED: Removed urgency field (vestige non utilise) et categories hr/quality

import { supabase } from './supabase';
import type { User, Site } from './supabase';
import { addBreadcrumb, captureException } from './sentry';

export interface Incident {
  id: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  site_id: string;
  category: 'client' | 'stock' | 'equipment' | 'other';
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  resolved_at: string | null;
  resolved_by: string | null;
  admin_notes: string | null;
}

export interface IncidentWithDetails extends Incident {
  user: Pick<User, 'id' | 'username' | 'first_name' | 'last_name' | 'role'>;
  site: Pick<Site, 'id' | 'name'>;
  resolved_by_user?: Pick<User, 'id' | 'username' | 'first_name' | 'last_name'> | null;
}

export interface IncidentResponse {
  success: boolean;
  error?: string;
  data?: Incident;
}

export interface IncidentsListResponse {
  success: boolean;
  error?: string;
  data?: IncidentWithDetails[];
}

export interface IncidentHistoryEntry {
  id: string;
  action_description: string;
  changed_at: string;
  changed_by_id: string | null;
  changed_by_name: string;
  old_status: string | null;
  new_status: string;
  action_type: string;
}

export interface IncidentHistoryResponse {
  success: boolean;
  error?: string;
  data?: IncidentHistoryEntry[];
}

// Fixed incident categories with icons and labels
export const INCIDENT_CATEGORIES = [
  {
    value: 'client' as const,
    label: 'Client',
    icon: 'Users',
    description: 'Problème avec un client',
    color: 'blue'
  },
  {
    value: 'stock' as const,
    label: 'Rupture de stock',
    icon: 'PackageX',
    description: 'Article manquant ou en rupture',
    color: 'orange'
  },
  {
    value: 'equipment' as const,
    label: 'Équipement',
    icon: 'Wrench',
    description: 'Appareil endommagé ou défaillant',
    color: 'red'
  },
  {
    value: 'other' as const,
    label: 'Autre',
    icon: 'HelpCircle',
    description: 'Autre type de problème',
    color: 'gray'
  }
] as const;

// Status options
export const INCIDENT_STATUSES = [
  {
    value: 'open' as const,
    label: 'Ouvert',
    color: 'blue'
  },
  {
    value: 'in_progress' as const,
    label: 'En cours',
    color: 'yellow'
  },
  {
    value: 'resolved' as const,
    label: 'Résolu',
    color: 'green'
  },
  {
    value: 'closed' as const,
    label: 'Fermé',
    color: 'gray'
  }
] as const;

// Create a new incident
export async function createIncident(
  category: Incident['category'] | null,
  description: string,
  siteId: string
): Promise<IncidentResponse> {
  addBreadcrumb('incident', 'Creation d\'un signalement', { category: category || 'other', siteId });
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Non authentifié' };
    }

    const { data, error } = await supabase
      .from('incidents')
      .insert({
        user_id: user.id,
        site_id: siteId,
        category: category || 'other',
        description: description.trim(),
        status: 'open'
      })
      .select()
      .single();

    if (error) {
      if (import.meta.env.DEV) console.error('Create incident error:', error);
      captureException(error, { context: 'createIncident' });
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    if (import.meta.env.DEV) console.error('Create incident error:', error);
    captureException(error, { context: 'createIncident' });
    return { success: false, error: 'Une erreur est survenue lors de la création du signalement' };
  }
}

// Get all incidents with filters (admin/director/manager/operator)
// Operators can only see their own incidents
// Managers can see their own incidents + incidents from their assigned sites
// Admins and directors can see all incidents
export async function getIncidents(filters?: {
  siteId?: string;
  category?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  searchTerm?: string;
}): Promise<IncidentsListResponse> {
  try {
    // Get current user to check role
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) {
      return { success: false, error: 'Non authentifié' };
    }

    // Get user profile to check role
    const { data: userProfile } = await supabase
      .from('users')
      .select('role')
      .eq('id', authUser.id)
      .maybeSingle();

    // If operator, use getMyIncidents instead
    if (userProfile?.role === 'operator') {
      return getMyIncidents();
    }

    // If manager, filter by assigned sites + own incidents
    let managerSiteIds: string[] = [];
    if (userProfile?.role === 'manager') {
      const { data: userSites, error: sitesError } = await supabase
        .from('user_sites')
        .select('site_id')
        .eq('user_id', authUser.id);

      if (sitesError) {
        if (import.meta.env.DEV) console.error('Error fetching manager sites:', sitesError);
        captureException(sitesError, { context: 'getIncidents.managerSites' });
        return { success: false, error: 'Erreur lors du chargement des sites' };
      }

      managerSiteIds = userSites?.map(us => us.site_id) || [];
    }

    let query = supabase
      .from('incidents')
      .select(`
        *,
        user:users!incidents_user_id_fkey(id, username, first_name, last_name, role),
        site:sites!incidents_site_id_fkey(id, name),
        resolved_by_user:users!incidents_resolved_by_fkey(id, username, first_name, last_name)
      `)
      .order('created_at', { ascending: false });

    // Apply manager site restriction (own incidents + assigned sites incidents)
    if (userProfile?.role === 'manager') {
      if (managerSiteIds.length > 0) {
        // Manager sees incidents from their assigned sites OR their own incidents
        query = query.or(`site_id.in.(${managerSiteIds.join(',')}),user_id.eq.${authUser.id}`);
      } else {
        // Manager has no assigned sites, only see their own incidents
        query = query.eq('user_id', authUser.id);
      }
    }

    // Apply filters
    if (filters?.siteId) {
      query = query.eq('site_id', filters.siteId);
    }
    if (filters?.category) {
      query = query.eq('category', filters.category);
    }
    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.startDate) {
      query = query.gte('created_at', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('created_at', filters.endDate);
    }
    if (filters?.searchTerm) {
      query = query.ilike('description', `%${filters.searchTerm}%`);
    }

    const { data, error } = await query;

    if (error) {
      if (import.meta.env.DEV) console.error('Get incidents error:', error);
      captureException(error, { context: 'getIncidents' });
      return { success: false, error: error.message };
    }

    return { success: true, data: data as unknown as IncidentWithDetails[] };
  } catch (error) {
    if (import.meta.env.DEV) console.error('Get incidents error:', error);
    captureException(error, { context: 'getIncidents' });
    return { success: false, error: 'Une erreur est survenue lors du chargement des incidents' };
  }
}

// Get single incident by ID
export async function getIncidentById(incidentId: string): Promise<{ success: boolean; error?: string; data?: IncidentWithDetails }> {
  try {
    const { data, error } = await supabase
      .from('incidents')
      .select(`
        *,
        user:users!incidents_user_id_fkey(id, username, first_name, last_name, role),
        site:sites!incidents_site_id_fkey(id, name),
        resolved_by_user:users!incidents_resolved_by_fkey(id, username, first_name, last_name)
      `)
      .eq('id', incidentId)
      .maybeSingle();

    if (error) {
      if (import.meta.env.DEV) console.error('Get incident error:', error);
      captureException(error, { context: 'getIncidentById' });
      return { success: false, error: error.message };
    }

    if (!data) {
      return { success: false, error: 'Incident non trouvé' };
    }

    return { success: true, data: data as unknown as IncidentWithDetails };
  } catch (error) {
    if (import.meta.env.DEV) console.error('Get incident error:', error);
    captureException(error, { context: 'getIncidentById' });
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Update incident status (admin only)
export async function updateIncidentStatus(
  incidentId: string,
  status: Incident['status']
): Promise<IncidentResponse> {
  addBreadcrumb('incident', 'Changement de statut de signalement', { incidentId, status });
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Non authentifié' };
    }

    const { data, error } = await supabase
      .from('incidents')
      .update({ status })
      .eq('id', incidentId)
      .select()
      .single();

    if (error) {
      if (import.meta.env.DEV) console.error('Update incident error:', error);
      captureException(error, { context: 'updateIncidentStatus' });
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    if (import.meta.env.DEV) console.error('Update incident error:', error);
    captureException(error, { context: 'updateIncidentStatus' });
    return { success: false, error: 'Une erreur est survenue lors de la mise à jour' };
  }
}

// Get incidents created by current user (for personal history - optional feature)
export async function getMyIncidents(): Promise<IncidentsListResponse> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Non authentifié' };
    }

    const { data, error } = await supabase
      .from('incidents')
      .select(`
        *,
        user:users!incidents_user_id_fkey(id, username, first_name, last_name, role),
        site:sites!incidents_site_id_fkey(id, name),
        resolved_by_user:users!incidents_resolved_by_fkey(id, username, first_name, last_name)
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      if (import.meta.env.DEV) console.error('Get my incidents error:', error);
      captureException(error, { context: 'getMyIncidents' });
      return { success: false, error: error.message };
    }

    return { success: true, data: data as unknown as IncidentWithDetails[] };
  } catch (error) {
    if (import.meta.env.DEV) console.error('Get my incidents error:', error);
    captureException(error, { context: 'getMyIncidents' });
    return { success: false, error: 'Une erreur est survenue' };
  }
}

// Utility functions for styling

// Get category color classes
export function getCategoryColorClass(category: Incident['category']): string {
  const colors: Record<Incident['category'], string> = {
    client: 'bg-blue-100 text-blue-800 border-blue-200',
    stock: 'bg-orange-100 text-orange-800 border-orange-200',
    equipment: 'bg-red-100 text-red-800 border-red-200',
    other: 'bg-gray-100 text-gray-800 border-gray-200'
  };
  return colors[category];
}

// Get status color classes
export function getStatusColorClass(status: Incident['status']): string {
  const colors: Record<Incident['status'], string> = {
    open: 'bg-blue-100 text-blue-800 border-blue-200',
    in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    resolved: 'bg-green-100 text-green-800 border-green-200',
    closed: 'bg-gray-100 text-gray-800 border-gray-200'
  };
  return colors[status];
}

// Get category info by value
export function getCategoryInfo(category: Incident['category']) {
  return INCIDENT_CATEGORIES.find(c => c.value === category);
}

// Get status info by value
export function getStatusInfo(status: Incident['status']) {
  return INCIDENT_STATUSES.find(s => s.value === status);
}

// Get incident history (status change logs)
export async function getIncidentHistory(incidentId: string): Promise<IncidentHistoryResponse> {
  try {
    const { data, error } = await supabase
      .rpc('get_incident_history', { p_incident_id: incidentId });

    if (error) {
      if (import.meta.env.DEV) console.error('Get incident history error:', error);
      captureException(error, { context: 'getIncidentHistory' });
      return { success: false, error: error.message };
    }

    return { success: true, data: data as IncidentHistoryEntry[] };
  } catch (error) {
    if (import.meta.env.DEV) console.error('Get incident history error:', error);
    captureException(error, { context: 'getIncidentHistory' });
    return { success: false, error: 'Une erreur est survenue lors du chargement de l\'historique' };
  }
}
